 <!-- Step 2 -->
<template>
  <div class="w-full max-w-xl m-auto tex-center">
    <!-- System -->
    <div>
      <p class="font-bold mb-4 text-lg">Select System</p>
      <div class="grid grid-cols-2">
        <a
          v-for="s in props.systems"
          :key="s.id"
          class="flex items-center border-solid border-2 m-2 p-2 font-medium cursor-pointer "
          :class="value.system == s.id ? 'shadow-lg border-blue-600 text-blue-600' : 'text-gray-400'"
          @click="update('form.system', s.id), moveTo('widths')"
        >
          <img :src="s.img" />
          <p class="pr-6">{{ s.name }}</p>
        </a>
        <div v-if="value.errors.system" class="form-error">{{ value.errors.system }}</div>
      </div>
    </div>

    <!-- Width -->
    <div v-if="value.system" id="widths">
      <div class="rounded w-full max-w-3xl m-auto h-px bg-blue-800 my-10">&nbsp;</div>
      <p class="font-bold mb-4 text-lg">Choose internal width channel</p>
      <div class="flex flex-row mt-6">
        <a
          v-for="(w,i) in props.widths"
          :key="i"
          class="border-solid border-2 m-2 px-2 pt-6 pb-8 w-1/3 cursor-pointer font-medium"
          :class="value.width == w.value ? 'shadow-lg border-blue-600 text-blue-600' : 'text-gray-400'"
          @click="update('form.width', w.value), moveTo('edges')"
        >
          <p class="p-2 text-center">{{ w.label }}</p>
        </a>
        <div v-if="value.errors.width" class="form-error">{{ value.errors.width }}</div>
      </div>
    </div>

    <!-- Edge -->
    <div v-if="value.width" id="edges">
      <div class="rounded w-full max-w-3xl m-auto h-px bg-gray-200 my-10">&nbsp;</div>
      <p class="font-bold mb-4 text-lg">Select Drainage Edge</p>
      <div class="flex flex-row mt-10">
        <a
          v-for="e in props.edges"
          :key="e.id"
          class="border-solid border-2 m-2 p-8 w-1/3 font-medium text-center"
          :class="value.edge == e.id ? 'shadow-lg border-blue-600 text-blue-600' : 'text-gray-400'"
          @click="update('form.edge', e.id), moveTo('classes')"
        >
          <p>{{ e.name }}</p>
        </a>
        <div v-if="value.errors.edge" class="form-error">{{ value.errors.edge }}</div>
      </div>
    </div>

    <!-- Class, Material, Type -->
    <div v-if="value.edge" id="classes" class="w-full max-w-3xl m-auto tex-center">
      <div class="rounded w-full max-w-3xl text-center m-auto h-px bg-blue-800 my-10">&nbsp;</div>
      <p class="font-bold mb-4 text-lg">Select Grate</p>
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-2 mt-10">
        <div class="flex flex-col">
          <p class="font-bold mb-6">Load Class</p>
          <a
            v-for="c in props.loadClass"
            :key="c.id"
            class="flex items-center border-solid border-2 m-1 px-2 py-2 text-gray-400 font-medium"
            :class="filterBy.loadClass.includes(c.id) ? 'shadow-lg border-blue-600 text-blue-600' : ''"
            @click="toggle(filterBy.loadClass, c.id), moveTo('grates')"
          >
            <class-icon
              :name="c.icon"
              class="flex-shrink-0 mx-2 w-8 h-8"
              :class="filterBy.loadClass.includes(c.id) ? 'fill-blue-800' : 'fill-gray-400'"
            />
            <p class="text-center">{{ c.name }}</p>
          </a>
        </div>

        <div v-if="props.materials.length > 0" class="flex flex-col">
          <p class="font-bold mb-6 text-center">Materials</p>
          <a
            v-for="m in props.materials"
            :key="m.id"
            class="border-solid border-2 m-1 px-1 py-2 text-gray-400 font-medium"
            :class="filterBy.materials.includes(m.id) ? 'shadow-lg border-blue-600 text-blue-600' : ''"
            @click="toggle(filterBy.materials, m.id)"
          >
            <p class="text-center">{{ m.name }}</p>
          </a>
        </div>

        <div v-if="props.types.length > 0" class="flex flex-col">
          <p class="font-bold mb-6 text-center">Types</p>
          <a
            v-for="t in props.types"
            :key="t.id"
            class="border-solid border-2 m-1 px-1 py-2 text-gray-400 font-medium"
            :class="filterBy.types.includes(t.id) ? 'shadow-lg border-blue-600 text-blue-600' : ''"
            @click="toggle(filterBy.types, t.id)"
          >
            <p class="text-center">{{ t.name }}</p>
          </a>
        </div>

        <div v-if="props.compliances.length > 0" class="flex flex-col">
          <p class="font-bold mb-6 text-center">Compliance</p>
          <a
            v-for="c in props.compliances"
            :key="c.id"
            class="flex items-center border-solid border-2 m-1 px-1 py-2 text-gray-400 font-medium"
            :class="filterBy.compliances.includes(c.id) ? 'shadow-lg border-blue-600 text-blue-600' : ''"
            @click="toggle(filterBy.compliances, c.id)"
          >
            <compliance-icon
              :name="c.icon"
              class="flex-shrink-0 mx-2 w-6 h-6"
              :class="filterBy.compliances.includes(c.id) ? 'fill-blue-800' : 'fill-gray-400'"
            />
            <p>{{ c.name }}</p>
          </a>
        </div>
      </div>
    </div>

    <!-- Grates -->
    <div v-if="value.edge" id="grates">
      <div class="rounded w-full max-w-3xl text-center m-auto h-px bg-blue-800 my-10">&nbsp;</div>
      <div class="grid grid-cols-2 sm:grid-cols-3 mt-10">
        <a
          v-for="(g, i) in props.grates"
          :key="i"
          class="text-center border-solid border-2 p-4 cursor-pointer "
          :class="value.grate && value.grate.cmt == g.cmt ? 'shadow-lg border-blue-600 text-blue-600' : 'text-gray-400'"
          @click="update('form.grate', g), moveTo('buttons-2')"
        >
          <img class="m-auto" :src="g.img" />
          <div class="my-4 text-center">
            <p class="font-bold text-gray-600 h-10">{{ g.label }}</p>
            <div class="flex items-center justify-center my-2">
              <span v-for="(icon,j) in g.compliances" :key="j" class="rounded bg-blue-800 w-4 h-4 mx-1">
                <compliance-icon :name="icon" class="flex-shrink-0 w-4 h-4 p-0.5 fill-white" />
              </span>
            </div>
            <ul v-for="(part,i) in g.parts" :key="i" class="text-left">
              <li class="text-gray-400 text-sm"> {{ part }}</li>
            </ul>
          </div>
        </a>
        <p v-if="props.grates.length == 0">
          No grates found.
        </p>
        <div v-if="value.errors.grate" class="form-error">{{ value.errors.grate }}</div>
      </div>
    </div>

    <div id="buttons-2" class="flex items-center justify-between px-8 py-4">
      <a class="btn-blue bg-gray-600" @click="update('step', value.step - 1)">Previous</a>
      <a class="btn-blue" :class="! value.grate ? 'opacity-50 cursor-not-allowed' :''" @click="value.grate ? update('step', value.step + 1) : ''">Next</a>
    </div>
    <!-- buttons -->
  </div>
</template>

<script>
import ClassIcon from '@/Shared/LoadClassesIcon'
import ComplianceIcon from '@/Shared/CompliancesIcon'


export default {
  components: { ClassIcon , ComplianceIcon },
  props: {
    value: Object,
    props: Object,
    filterBy: Object,
  },
  emits: ['input', 'move'],
  mounted() {
    console.log('[DEBUG] Edges received in StepTwo:', this.props.edges)
  },
  methods: {
    update(key, value) {
      this.$emit('input', { key, value })
    },
    toggle(array, value) {
      let idx = array.indexOf(value)
      if (idx === -1) {
        array.push(value)
      }
      else {
        array.splice(idx, 1)
      }
    },
    moveTo(value) {
      this.$emit('move', value)
    },
  },
}

</script>
